import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { RELEASE_NOTES, type ReleaseNote, type ReleaseNoteType } from '../releaseNotes';

const STORAGE_KEY = 'insight:seenBuild';

const TYPE_CONFIG: Record<ReleaseNoteType, { label: string; color: string; bg: string }> = {
  feature:     { label: 'Neu',          color: '#2563eb', bg: 'rgba(37,99,235,0.1)'  },
  improvement: { label: 'Verbessert',   color: '#059669', bg: 'rgba(5,150,105,0.1)'  },
  fix:         { label: 'Behoben',      color: '#d97706', bg: 'rgba(217,119,6,0.1)'  },
};

function ReleaseSection({ note, isLatest }: { note: ReleaseNote; isLatest: boolean }) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <span style={{
          fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
          padding: '0.125rem 0.5rem', borderRadius: '9999px',
          background: isLatest ? 'var(--color-primary)' : 'var(--color-border)',
          color: isLatest ? '#fff' : 'var(--color-text-muted)',
        }}>
          v{note.version}
        </span>
        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text)' }}>
          {note.title}
        </span>
        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginLeft: 'auto' }}>
          {new Date(note.date).toLocaleString('de-DE', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })} Uhr
        </span>
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {note.items.map((item, i) => {
          const cfg = TYPE_CONFIG[item.type];
          return (
            <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', fontSize: '0.875rem' }}>
              <span style={{
                flexShrink: 0, marginTop: '0.125rem',
                fontSize: '0.6875rem', fontWeight: 600,
                padding: '0.125rem 0.375rem', borderRadius: '4px',
                background: cfg.bg, color: cfg.color,
                whiteSpace: 'nowrap',
              }}>
                {cfg.label}
              </span>
              <span style={{ color: 'var(--color-text)', lineHeight: 1.5 }}>{item.text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface ReleaseNotesModalProps {
  isOpen: boolean;
  onClose: (dismiss: boolean) => void;
}

function ReleaseNotesModalInner({ isOpen, onClose }: ReleaseNotesModalProps) {
  const [dismiss, setDismiss] = useState(false);

  const handleClose = useCallback(() => onClose(dismiss), [onClose, dismiss]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.55)', padding: '1rem',
        animation: 'insight-fade-in 0.15s ease-out',
      }}
      onClick={handleClose}
    >
      <style>{`@keyframes insight-fade-in{from{opacity:0}to{opacity:1}}@keyframes insight-slide-up{from{transform:translateY(16px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Release Notes"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '560px', maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          background: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
          animation: 'insight-slide-up 0.18s ease-out',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid var(--color-border)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            <h2 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 700, color: 'var(--color-text)' }}>
              Was ist neu?
            </h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, fontSize: '1.375rem', lineHeight: 1,
              background: 'none', border: 'none', borderRadius: 'var(--radius-sm)',
              cursor: 'pointer', color: 'var(--color-text-muted)',
            }}
            aria-label="Schließen"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.25rem 1.5rem', overflowY: 'auto', flex: 1 }}>
          {RELEASE_NOTES.slice(0, 3).map((note, i) => (
            <ReleaseSection key={note.version} note={note} isLatest={i === 0} />
          ))}

          {RELEASE_NOTES.length > 3 && (
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{
                fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--color-text-muted)',
                marginBottom: '0.5rem',
              }}>
                Ältere Versionen
              </div>
              {RELEASE_NOTES.slice(3).map((note) => (
                <div key={note.version} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.375rem 0',
                  borderBottom: '1px solid var(--color-border)',
                  fontSize: '0.8125rem',
                }}>
                  <span style={{
                    fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase',
                    padding: '0.1rem 0.4rem', borderRadius: '9999px',
                    background: 'var(--color-border)', color: 'var(--color-text-muted)',
                    flexShrink: 0,
                  }}>
                    v{note.version}
                  </span>
                  <span style={{ color: 'var(--color-text)', flex: 1 }}>{note.title}</span>
                  <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem', flexShrink: 0 }}>
                    {new Date(note.date).toLocaleDateString('de-DE')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.5rem',
          borderTop: '1px solid var(--color-border)',
          flexShrink: 0,
          gap: '1rem',
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={dismiss}
              onChange={(e) => setDismiss(e.target.checked)}
              style={{ width: 15, height: 15, accentColor: 'var(--color-primary)', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
              Nicht mehr anzeigen
            </span>
          </label>
          <button
            onClick={handleClose}
            style={{
              padding: '0.5rem 1.25rem', fontSize: '0.875rem', fontWeight: 600,
              background: 'var(--color-primary)', color: '#fff',
              border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
            }}
          >
            Verstanden
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Hook für AppLayout
export function useReleaseNotes() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    if (seen !== __APP_BUILD_ID__) {
      setIsOpen(true);
    }
  }, []);

  const handleClose = useCallback((dismiss: boolean) => {
    if (dismiss) {
      localStorage.setItem(STORAGE_KEY, __APP_BUILD_ID__);
    }
    setIsOpen(false);
  }, []);

  return { isOpen, handleClose };
}

export function ReleaseNotesModal() {
  const { isOpen, handleClose } = useReleaseNotes();
  return <ReleaseNotesModalInner isOpen={isOpen} onClose={handleClose} />;
}
